import { timelyCreateApiBase as api } from "../../../../api/core/timely/timely.createApi.base";
export const addTagTypes = ["AutoSheet"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listTimesheetProposals: build.query<
        ListTimesheetProposalsApiResponse,
        ListTimesheetProposalsApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals`,
          params: {
            status: queryArg.status,
            date_from: queryArg.dateFrom,
            date_to: queryArg.dateTo,
            timely_user_id: queryArg.timelyUserId,
            page: queryArg.page,
            limit: queryArg.limit,
          },
        }),
        providesTags: ["AutoSheet"],
      }),
      getTimesheetProposal: build.query<
        GetTimesheetProposalApiResponse,
        GetTimesheetProposalApiArg
      >({
        query: (queryArg) => ({ url: `/timesheet_proposals/${queryArg}` }),
        providesTags: ["AutoSheet"],
      }),
      deleteTimesheetProposal: build.mutation<
        DeleteTimesheetProposalApiResponse,
        DeleteTimesheetProposalApiArg
      >({
        query: (queryArg) => ({ url: `/timesheet_proposals/${queryArg}`, method: "DELETE" }),
        invalidatesTags: ["AutoSheet"],
      }),
      submitTimesheetProposal: build.mutation<
        SubmitTimesheetProposalApiResponse,
        SubmitTimesheetProposalApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/${queryArg.id}/submit`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["AutoSheet"],
      }),
      unsubmitTimesheetProposal: build.mutation<
        UnsubmitTimesheetProposalApiResponse,
        UnsubmitTimesheetProposalApiArg
      >({
        query: (queryArg) => ({ url: `/timesheet_proposals/${queryArg}/unsubmit`, method: "POST" }),
        invalidatesTags: ["AutoSheet"],
      }),
      generateTimesheetProposal: build.mutation<
        GenerateTimesheetProposalApiResponse,
        GenerateTimesheetProposalApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/generate`,
          method: "POST",
          body: queryArg,
        }),
        invalidatesTags: ["AutoSheet"],
      }),
      regenerateEntryNotes: build.mutation<
        RegenerateEntryNotesApiResponse,
        RegenerateEntryNotesApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/regenerate-notes`,
          method: "POST",
          body: queryArg,
        }),
        invalidatesTags: ["AutoSheet"],
      }),
      createTimesheetProposalEntry: build.mutation<
        CreateTimesheetProposalEntryApiResponse,
        CreateTimesheetProposalEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/${queryArg.proposalId}/entries`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["AutoSheet"],
      }),
      deleteTimesheetProposalEntry: build.mutation<
        DeleteTimesheetProposalEntryApiResponse,
        DeleteTimesheetProposalEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/entries/${queryArg}`,
          method: "DELETE",
        }),
        invalidatesTags: ["AutoSheet"],
      }),
      patchTimesheetProposalEntry: build.mutation<
        PatchTimesheetProposalEntryApiResponse,
        PatchTimesheetProposalEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/timesheet_proposals/entries/${queryArg.entryId}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["AutoSheet"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as timelyApi };
export type ListTimesheetProposalsApiResponse = /** status 200 Proposals retrieved successfully */ {
  proposals: {
    id: string;
    date: string;
    status: "pending" | "accepted" | "rejected" | "edited";
    created_at?: string;
    updated_at?: string | null;
    entry_count?: number;
    total_duration_minutes?: number;
  }[];
  generation_requests?: {
    id: string;
    date: string;
    status: "running" | "completed" | "failed" | "timed_out";
    generation_mode: "manual" | "auto";
    requested_at?: string;
    retry_after_at?: string | null;
    error_message?: string | null;
  }[];
  meta?: object;
};
export type ListTimesheetProposalsApiArg = {
  /** Filter by status */
  status?: "pending" | "accepted" | "rejected" | "edited";
  /** Filter proposals on or after this date (YYYY-MM-DD) */
  dateFrom?: string;
  /** Filter proposals on or before this date (YYYY-MM-DD) */
  dateTo?: string;
  /** Filter proposals by Timely user id */
  timelyUserId?: number;
  /** Page number (1-based) */
  page?: number;
  /** Page size */
  limit?: number;
};
export type GetTimesheetProposalApiResponse = /** status 200 Proposal retrieved successfully */ {
  proposal: {
    id: string;
    date: string;
    user_id?: number;
    status: "pending" | "accepted" | "rejected" | "edited";
    log?: object[];
    metadata?: object;
    created_at?: string;
    updated_at?: string | null;
    entries?: {
      id: number;
      timesheet_proposal_id: string;
      date: string;
      timely_project_id?: number | null;
      timely_label_ids?: number[];
      started_at?: string | null;
      ended_at?: string | null;
      hour_id?: number | null;
      note?: string | null;
      duration_minutes?: number | null;
      resource_id?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
    items?: {
      id: number;
      timesheet_proposal_id: string;
      timesheet_proposal_entry_id?: number | null;
      started_at?: string | null;
      ended_at?: string | null;
      note?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
  };
};
export type GetTimesheetProposalApiArg = /** Proposal ID (uuid) */ string;
export type DeleteTimesheetProposalApiResponse = /** status 200 Proposal deleted successfully */ {
  message?: string;
};
export type DeleteTimesheetProposalApiArg = /** Proposal ID (uuid) */ string;
export type SubmitTimesheetProposalApiResponse = /** status 200 Proposal submitted */ {
  proposal: {
    id: string;
    date: string;
    user_id?: number;
    status: "pending" | "accepted" | "rejected" | "edited";
    log?: object[];
    metadata?: object;
    created_at?: string;
    updated_at?: string | null;
    entries?: {
      id: number;
      timesheet_proposal_id: string;
      date: string;
      timely_project_id?: number | null;
      timely_label_ids?: number[];
      started_at?: string | null;
      ended_at?: string | null;
      hour_id?: number | null;
      note?: string | null;
      duration_minutes?: number | null;
      resource_id?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
    items?: {
      id: number;
      timesheet_proposal_id: string;
      timesheet_proposal_entry_id?: number | null;
      started_at?: string | null;
      ended_at?: string | null;
      note?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
  };
  created_hour_ids: number[];
};
export type SubmitTimesheetProposalApiArg = {
  /** Proposal ID (uuid) */
  id: string;
  body: {
    /** Per-entry external_links the FE resolved (Option a2). Each item is keyed to a persisted proposal entry by its id. */
    entries?: {
      /** timesheet_proposal_entry id this link-set belongs to */
      id: number;
      external_links?: {
        provider_type?: string;
        provider_id?: string | null;
        external_id?: string | null;
        uri?: string;
        data?: object;
      }[];
    }[];
  };
};
export type UnsubmitTimesheetProposalApiResponse = /** status 200 Proposal unsubmitted */ {
  proposal: {
    id: string;
    date: string;
    user_id?: number;
    status: "pending" | "accepted" | "rejected" | "edited";
    log?: object[];
    metadata?: object;
    created_at?: string;
    updated_at?: string | null;
    entries?: {
      id: number;
      timesheet_proposal_id: string;
      date: string;
      timely_project_id?: number | null;
      timely_label_ids?: number[];
      started_at?: string | null;
      ended_at?: string | null;
      hour_id?: number | null;
      note?: string | null;
      duration_minutes?: number | null;
      resource_id?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
    items?: {
      id: number;
      timesheet_proposal_id: string;
      timesheet_proposal_entry_id?: number | null;
      started_at?: string | null;
      ended_at?: string | null;
      note?: string | null;
      metadata?: object;
      created_at?: string;
      updated_at?: string | null;
    }[];
  };
  partial_revert: boolean;
  deleted_count: number;
  expected_count: number;
};
export type UnsubmitTimesheetProposalApiArg = /** Proposal ID (uuid) */ string;
export type GenerateTimesheetProposalApiResponse = /** status 202 Generation started */ {
  message: string;
  job_id?: string;
  estimated_completion_time?: string;
};
export type GenerateTimesheetProposalApiArg = {
  date: string;
  timely_user_id?: number;
  timely_account_id?: number;
  generation_mode?: "manual" | "auto";
  force_regenerate?: boolean;
  session_id?: number | null;
  metadata?: {
    effort?: string;
    interval_minutes?: string;
    provider?: string;
    note_language?: string;
  };
};
export type RegenerateEntryNotesApiResponse = /** status 202 Note regeneration started */ {
  message: string;
  job_id?: string;
  estimated_completion_time?: string;
};
export type RegenerateEntryNotesApiArg = {
  date: string;
  timely_account_id: number;
  timely_user_id: number;
  /** Optional entry ID to regenerate a single entry; omit to regenerate all edited entries on the date. */
  entry_id?: string | null;
  session_id?: number | null;
  metadata?: {
    note_language?: string;
  };
};
export type CreateTimesheetProposalEntryApiResponse = /** status 201 Entry created successfully */ {
  entry: {
    id: number;
    timesheet_proposal_id: string;
    date: string;
    timely_project_id?: number | null;
    timely_label_ids?: number[];
    started_at?: string | null;
    ended_at?: string | null;
    hour_id?: number | null;
    note?: string | null;
    duration_minutes?: number | null;
    resource_id?: string | null;
    metadata?: object;
    created_at?: string;
    updated_at?: string | null;
  };
  operation_metadata: {
    was_reused: boolean;
    action: "created_new_entry" | "reused_existing_entry";
    message: string;
  };
};
export type CreateTimesheetProposalEntryApiArg = {
  /** Parent proposal ID (uuid) */
  proposalId: string;
  body: {
    date?: string;
    timely_project_id?: number | null;
    timely_label_ids?: number[];
    note?: string | null;
    duration_minutes?: number | null;
    resource_id?: string | null;
    started_at?: string | null;
    ended_at?: string | null;
  };
};
export type DeleteTimesheetProposalEntryApiResponse = /** status 200 Entry deleted successfully */ {
  message: string;
  deleted_entry_id: number;
};
export type DeleteTimesheetProposalEntryApiArg = /** Proposal entry ID */ string;
export type PatchTimesheetProposalEntryApiResponse = /** status 200 Entry patched successfully */ {
  timely_project_id?: number;
  timely_label_ids?: number[];
  note?: string | null;
  duration_minutes?: number | null;
  hour_id?: number | null;
  resource_id?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  updated_at?: string | null;
};
export type PatchTimesheetProposalEntryApiArg = {
  /** Proposal entry ID */
  entryId: string;
  body: {
    timely_project_id?: number;
    timely_label_ids?: number[];
    note?: string | null;
    duration_minutes?: number | null;
    hour_id?: number | null;
    resource_id?: string | null;
    started_at?: string | null;
    ended_at?: string | null;
  };
};
export const {
  useListTimesheetProposalsQuery,
  useGetTimesheetProposalQuery,
  useDeleteTimesheetProposalMutation,
  useSubmitTimesheetProposalMutation,
  useUnsubmitTimesheetProposalMutation,
  useGenerateTimesheetProposalMutation,
  useRegenerateEntryNotesMutation,
  useCreateTimesheetProposalEntryMutation,
  useDeleteTimesheetProposalEntryMutation,
  usePatchTimesheetProposalEntryMutation,
} = injectedRtkApi;
